import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  mergeCustomers,
  type DuplicateCustomerCard,
  type DuplicateGroup,
} from '@/lib/api/customers.js';
import { formatCurrency } from '@/lib/format.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError, toastSuccess } from '@/lib/toast.js';
import { useManagerOverride } from '@/hooks/useManagerOverride.js';
import { ManagerOverrideModal } from '@/components/common/ManagerOverrideModal.js';

const FIELDS = [
  { key: 'firstName', label: 'First name' },
  { key: 'lastName', label: 'Last name' },
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Phone' },
  { key: 'address', label: 'Address' },
] as const;

function formatAddress(c: DuplicateCustomerCard): string {
  const line = [c.address, c.city, c.state, c.zip].filter(Boolean).join(', ');
  return line || '—';
}

function fieldValue(c: DuplicateCustomerCard, key: string): string {
  if (key === 'firstName') return c.firstName || '—';
  if (key === 'lastName') return c.lastName || '—';
  if (key === 'email') return c.email || '—';
  if (key === 'phone') return c.phone || '—';
  if (key === 'address') return formatAddress(c);
  return '—';
}

function expandFieldSelections(selections: Record<string, string>): Record<string, string> {
  const next = { ...selections };
  const addressSource = selections.address;
  if (addressSource) {
    next.city = addressSource;
    next.state = addressSource;
    next.zip = addressSource;
  }
  return next;
}

export function MergeCustomersModal({
  group,
  onClose,
  onMerged,
}: {
  group: DuplicateGroup;
  onClose: () => void;
  onMerged: (result?: { primaryId: string }) => void;
}) {
  const { requireOverride, modalProps } = useManagerOverride();
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(group.customers.map((c) => c.id)),
  );
  const [primaryId, setPrimaryId] = useState(
    () =>
      [...group.customers].sort(
        (a, b) => b.ticketCount - a.ticketCount || a.createdAt.localeCompare(b.createdAt),
      )[0]!.id,
  );
  const [fieldSelections, setFieldSelections] = useState<Record<string, string>>(() => {
    const init: Record<string, string> = {};
    for (const f of FIELDS) init[f.key] = primaryId;
    return init;
  });
  const [reason, setReason] = useState('Duplicate customer consolidation');

  const selectedCustomers = group.customers.filter((c) => selected.has(c.id));
  const multiHistory = selectedCustomers.filter((c) => c.ticketCount > 0).length >= 2;
  const flagged = selectedCustomers.some((c) => c.customerStatus === 'flagged');
  const totalTickets = selectedCustomers.reduce((s, c) => s + c.ticketCount, 0);
  const totalPts = selectedCustomers.reduce((s, c) => s + c.loyaltyPoints, 0);
  const totalCredit = selectedCustomers.reduce((s, c) => s + Number(c.storeCredit || 0), 0);

  const mut = useMutation({
    mutationFn: () =>
      mergeCustomers({
        primaryId,
        duplicateIds: [...selected].filter((id) => id !== primaryId),
        fieldSelections: expandFieldSelections(fieldSelections),
        reason,
      }),
    onSuccess: (data) => {
      toastSuccess('Customers merged');
      onMerged({ primaryId: data.primaryId });
      onClose();
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  const handleMerge = async () => {
    if (selected.size < 2) {
      toastError('Select at least two customers');
      return;
    }
    if (!selected.has(primaryId)) {
      toastError('Survivor must be selected');
      return;
    }
    const override = await requireOverride({
      action: 'customer.merge',
      title: 'Merge customers',
      description: `Merge ${selected.size} customer records into one. This reassigns tickets and ledgers.`,
      requiredRole: 'store_manager',
      context: { reason },
    });
    if (!override.approved) return;
    mut.mutate();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-6 shadow-xl">
        <h2 className="text-lg font-bold text-slate-950">Review & merge</h2>
        <p className="mt-1 text-sm text-slate-500">
          Matched on {group.matchedOn} ({group.confidence} · {group.strategy.replace(/_/g, ' ')})
        </p>

        {multiHistory ? (
          <p className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            ⚠ More than one record has ticket history — confirm these are the same person before
            merging.
          </p>
        ) : null}

        {group.warningLabel ||
        group.strategy === 'phone_family' ||
        group.strategy === 'phone_shared' ? (
          <p className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900">
            ⚠ These may be different people sharing a phone number (e.g. family members). Verify
            before merging.
            {group.warningLabel ? ` (${group.warningLabel})` : ''}
          </p>
        ) : null}

        <div className="mt-4 space-y-2">
          <p className="text-xs font-semibold uppercase text-slate-500">Survivor</p>
          {selectedCustomers.map((c) => (
            <label
              key={c.id}
              className="flex cursor-pointer items-start gap-2 rounded-lg border border-slate-200 p-3"
            >
              <input
                type="radio"
                name="primary"
                checked={primaryId === c.id}
                onChange={() => {
                  setPrimaryId(c.id);
                  setFieldSelections(() => {
                    const next: Record<string, string> = {};
                    for (const f of FIELDS) next[f.key] = c.id;
                    return next;
                  });
                }}
              />
              <span className="text-sm">
                <span className="font-semibold">
                  {c.firstName} {c.lastName}
                </span>{' '}
                · {c.ticketCount} tickets · {formatCurrency(c.totalSpend)} · {c.loyaltyPoints} pts
                {c.customerStatus === 'flagged' ? (
                  <span className="ml-2 text-xs font-bold text-red-600">FLAGGED</span>
                ) : null}
              </span>
            </label>
          ))}
        </div>

        <div className="mt-4">
          <p className="text-xs font-semibold uppercase text-slate-500">Include in merge</p>
          <div className="mt-2 space-y-1">
            {group.customers.map((c) => (
              <label key={c.id} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={selected.has(c.id)}
                  onChange={(e) => {
                    setSelected((prev) => {
                      const next = new Set(prev);
                      if (e.target.checked) next.add(c.id);
                      else next.delete(c.id);
                      return next;
                    });
                  }}
                />
                {c.firstName} {c.lastName} ({c.ticketCount} tickets)
              </label>
            ))}
          </div>
        </div>

        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b text-xs uppercase text-slate-500">
                <th className="py-2 pr-2">Field</th>
                {selectedCustomers.map((c) => (
                  <th key={c.id} className="py-2 pr-2">
                    {c.firstName}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {FIELDS.map((f) => (
                <tr key={f.key} className="border-b border-slate-100">
                  <td className="py-2 pr-2 font-medium">{f.label}</td>
                  {selectedCustomers.map((c) => (
                    <td key={c.id} className="py-2 pr-2">
                      <label className="flex items-center gap-1">
                        <input
                          type="radio"
                          name={`field-${f.key}`}
                          checked={fieldSelections[f.key] === c.id}
                          onChange={() =>
                            setFieldSelections((prev) => ({ ...prev, [f.key]: c.id }))
                          }
                        />
                        <span className="truncate">{fieldValue(c, f.key)}</span>
                      </label>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-4 rounded-lg bg-slate-50 p-3 text-sm text-slate-700">
          After merge: {totalTickets} tickets · {totalPts} loyalty pts ·{' '}
          {formatCurrency(totalCredit)} credit
          {flagged ? (
            <span className="mt-1 block font-semibold text-red-700">
              ⚠ Retains FLAGGED status from a merged record
            </span>
          ) : null}
        </div>

        <label className="mt-4 block text-sm">
          Reason
          <input
            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </label>

        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
            disabled={mut.isPending}
            onClick={() => void handleMerge()}
          >
            Merge {selected.size} customers →
          </button>
        </div>
      </div>
      {modalProps ? <ManagerOverrideModal {...modalProps} /> : null}
    </div>
  );
}
